import type { BookingDetailsData, RebookingData } from '../../domain/types'
import type { BookingDetailsAPIResponse } from '../types'
import { formatDestination } from '@/scopes/account/common/formatters'
import { accommodationsMapper } from './accommodationsMapper'
import { busInformationMapper } from './busInformationMapper'
import { extrasMapper } from './extrasMapper'
import { flightInformationMapper } from './flightInformationMapper'
import { groupInfoMapper } from './groupInfoMapper'
import { participantsMapper } from './participantsMapper'
import { paymentMapper } from './paymentMapper'
import { transferDirectionsMapper, transferTypeMapper } from './transfersMapper'
import { travelGuideMapper } from './travelGuideMapper'

function transportTypeMapper(apiResponse: BookingDetailsAPIResponse): BookingDetailsData['transportType'] {
  if (apiResponse.flightInformation?.length)
    return 'flight'
  if (apiResponse.busInformation)
    return 'bus'
  return 'own-transport'
}

export function bookingDetailsMapper(apiResponse: BookingDetailsAPIResponse): BookingDetailsData {
  const firstAccommodationAddress = apiResponse.accommodations?.[0]?.address
  const bookingNumber = apiResponse.bookingNumber?.toString() ?? ''
  const { extras, specialLuggageExtras } = extrasMapper(apiResponse.services)
  const preferences = apiResponse.preferences ?? []
  const groupInfo = groupInfoMapper(apiResponse.groupInfo)
  const isSelfServiceEnabled = apiResponse.isSelfServiceEnabled === true
  const areServicesEditable = apiResponse.areServicesEditable === true
  const isCancelled = apiResponse.status?.toLowerCase() === 'cancelled'
  const isAddedByYou = apiResponse.isBookingAddedByYou === true
  const collection = apiResponse.collection?.trim().toLowerCase() ?? ''
  const participants = participantsMapper(apiResponse.participants, apiResponse.services)
  const bookingParticipantIds = apiResponse.participants?.filter(participant => participant.id != null).map(participant => String(participant.id)) ?? []
  const payment = paymentMapper(apiResponse.payment)
  const accommodations = accommodationsMapper(apiResponse.accommodations)
  const rebookedBookingNumber = apiResponse.rebookedBookingNumber?.toString() ?? ''
  const rebooking: RebookingData | null = rebookingMapper(apiResponse.rebooking)
  const { hasOutboundTransfer, hasInboundTransfer } = transferDirectionsMapper(apiResponse.services)
  const cancellationOptions = {
    isFullCancellable: apiResponse.cancellationOptions?.isFullCancellable ?? false,
    isPartialCancellable: apiResponse.cancellationOptions?.isPartialCancellable ?? false,
    isRebookable: apiResponse.cancellationOptions?.isRebookable ?? false,
    voucherCancellationUrl: apiResponse.cancellationOptions?.voucherCancellationUrl ?? null,
  }

  const hasCarRental = apiResponse.services?.some(service => service.type === 'CarRental') ?? false

  return {
    bookingNumber,
    customerId: apiResponse.customerId || null,
    accommodations,
    groupName: groupInfo.groupName,
    groupSize: groupInfo.groupSize,
    destination: formatDestination({
      cityName: firstAccommodationAddress?.city,
      regionName: firstAccommodationAddress?.region,
      countryName: firstAccommodationAddress?.country,
    }),
    rawDepartureDate: apiResponse.departureDate || '',
    rawReturnDate: apiResponse.returnDate || '',
    isTotallySnow: collection === 'totally_snow',
    isPrimavera: collection === 'primavera',
    participants,
    bookingParticipantIds,
    payment,
    isGroupParticipant: groupInfo.isGroupParticipant,
    isGroupResponsible: groupInfo.isGroupResponsible,
    isGroupBooking: groupInfo.isGroupBooking,
    isSelfServiceEnabled,
    areServicesEditable,
    isCancelled,
    isAddedByYou,
    groupSubscriptionModel: groupInfo.groupSubscriptionModel,
    isMasterBooking: groupInfo.isMasterBooking,
    extras,
    specialLuggageExtras,
    preferences,
    customerSupportOpeningHours: apiResponse.contactInformation?.customerSupportOpeningHours ?? '',
    travelGuideContactInfo: travelGuideMapper(apiResponse.contactInformation),
    latitude: apiResponse.excursions?.latitude?.toString() || '',
    longitude: apiResponse.excursions?.longitude?.toString() || '',
    canAccountBeDeleted: apiResponse.canAccountBeDeleted || false,
    rebookedBookingNumber,
    rebooking,
    transferType: transferTypeMapper(apiResponse.services),
    hasOutboundTransfer,
    hasInboundTransfer,
    transportType: transportTypeMapper(apiResponse),
    hasCarRental,
    busInformation: busInformationMapper(apiResponse.busInformation ?? undefined),
    flightInformation: flightInformationMapper(apiResponse.flightInformation),
    cancellationOptions,
  }
}

function rebookingMapper(rebooking: BookingDetailsAPIResponse['rebooking']): RebookingData | null {
  return rebooking
    ? {
        state: rebooking.state === 'OnlineRebookable' ? 'online' : 'manual',
        hasAccommodationAvailability: rebooking.hasAccommodationAvailability,
      }
    : null
}
